import { useEffect, useMemo, useState } from "react";
import AppShell from "./components/AppShell";
import HomePage from "./pages/HomePage";
import Transactions from "./pages/Transactions";
import TasksPage from "./pages/TasksPage";
import CalendarPage from "./pages/CalendarPage";
import FormsPage from "./pages/FormsPage";
import DealIntakeModal from "./features/deals/DealIntakeModal";
import Release2BHomeRecovery from "./features/intake/Release2BHomeRecovery";
import Release2BIntake from "./features/intake/Release2BIntakeGuarded";
import Release2BLeadershipReview from "./features/intake/Release2BLeadershipReview";
import Release2BOperations from "./features/intake/Release2BOperations";
import Release2BRecovery, { startRelease2BRecovery } from "./features/intake/Release2BRecovery";
import { normalizeRelease2BTransition } from "./features/intake/release2BInvariants";
import { createListingRecord, createSession } from "./features/intake/release2BState";
import TransactionCommandCenter from "./features/transactions/readonly/TransactionCommandCenter";
import { EMPTY_SUMMARY } from "./domain/workflows";
import { isLeadership, normalizeRole } from "./domain/roles";
import { api } from "./services/api";

const FALLBACK_USER = { email: "", name: "Team Member", role: "AGENT", title: "Agent" };
const PAGE_PATHS = { home: "/", transactions: "/transactions", tasks: "/tasks", calendar: "/calendar", forms: "/forms" };

function routeFromPath(pathname = "/") {
  const path = pathname.replace(/\/+$/, "") || "/";
  const transactionMatch = path.match(/^\/transactions\/([^/]+)$/);
  if (transactionMatch) return { page: "transactions", transactionId: decodeURIComponent(transactionMatch[1]), path };
  if (path === "/transactions") return { page: "transactions", transactionId: "", path };
  if (path === "/tasks") return { page: "tasks", transactionId: "", path };
  if (path === "/calendar") return { page: "calendar", transactionId: "", path };
  if (path === "/forms") return { page: "forms", transactionId: "", path };
  if (path === "/") return { page: "home", transactionId: "", path };
  return { page: path.slice(1) || "home", transactionId: "", path };
}
function pathForTarget(target) { if (String(target || "").startsWith("/")) return target; return PAGE_PATHS[target] || `/${target}`; }
function ComingSoonPage() { return <section className="panel" style={{ margin: "32px", padding: "36px" }}><h1 style={{ marginTop: 0 }}>This ROOTS area is coming soon.</h1><p>The navigation is available, but this module has not been approved for production yet.</p></section>; }
function BootstrapFailure({ message, onRetry }) { return <section className="roots-auth-failure"><article><span className="roots-auth-mark" aria-hidden="true">R</span><span className="transactions-eyebrow">Authentication blocked</span><h1>ROOTS could not verify your session.</h1><p>No ROOTS navigation, transaction, client, deadline, document, financial, or fallback identity information is displayed.</p><small>{message}</small><button type="button" className="transactions-primary-action" onClick={onRetry}>Retry authentication</button></article></section>; }

export default function App() {
  const [route, setRoute] = useState(() => routeFromPath(window.location.pathname));
  const [workflow, setWorkflow] = useState(null);
  const [release2BSession, setRelease2BSession] = useState(null);
  const [release2BOpen, setRelease2BOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ user: FALLBACK_USER, summary: EMPTY_SUMMARY, transactions: [], tasks: [], transactionTasks: [], transactionActivity: [], transactionCalendar: [], checklistPhaseDataAvailable: false, calendar: [], agents: [] });
  const leadership = useMemo(() => isLeadership(data.user), [data.user]);
  const normalizedRole = normalizeRole(data.user?.role);

  async function load() {
    setLoading(true); setError("");
    try {
      const bootstrapData = await api.bootstrap();
      const readOnlyData = await api.readOnlyTransactions();
      setData({ ...bootstrapData, transactions: readOnlyData.transactions || [], transactionTasks: readOnlyData.tasks || [], transactionActivity: [], transactionCalendar: readOnlyData.calendar || [], checklistPhaseDataAvailable: Boolean(readOnlyData.checklist?.phaseDataAvailable) });
    } catch (err) { setError(err.message || "ROOTS could not load production data."); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { const handlePopState = () => setRoute(routeFromPath(window.location.pathname)); window.addEventListener("popstate", handlePopState); return () => window.removeEventListener("popstate", handlePopState); }, []);

  function navigate(target, options = {}) {
    const path = pathForTarget(target);
    if (options.replace) window.history.replaceState({}, "", path); else window.history.pushState({}, "", path);
    setRoute(routeFromPath(path)); window.scrollTo({ top: 0, behavior: "auto" });
  }
  function updateRelease2BSession(updater) {
    setRelease2BSession(current => {
      const proposed = typeof updater === "function" ? updater(current) : updater;
      return normalizeRelease2BTransition(current, proposed);
    });
  }
  function startWorkflow(selectedWorkflow) {
    if (selectedWorkflow?.id === "listing-agreement") { setRelease2BSession(createSession(createListingRecord())); setRelease2BOpen(true); return; }
    if (selectedWorkflow?.id === "pending-without-record") { setRelease2BSession({ ...createSession(null), pendingSideChoice: true }); setRelease2BOpen(true); return; }
    setWorkflow(selectedWorkflow);
  }
  function resumeRelease2B(stream = "base") {
    if (!release2BSession) return;
    if (stream === "marketing") updateRelease2BSession(current => startRelease2BRecovery(current, "marketing"));
    else if (stream === "openHouse") updateRelease2BSession(current => startRelease2BRecovery(current, "openHouse", "edit"));
    else if (stream === "openHouseReplacement") updateRelease2BSession(current => startRelease2BRecovery(current, "openHouse", "replacement"));
    else updateRelease2BSession(current => ({ ...current, activeCorrection: null }));
    setRelease2BOpen(true);
  }

  const common = { user: data.user, leadership, summary: data.summary, transactions: data.transactions, tasks: data.tasks, calendar: data.calendar, onStart: startWorkflow, onNavigate: navigate, onRefresh: load };
  const pages = {
    home: <><HomePage {...common} /><Release2BHomeRecovery session={release2BSession} onResume={resumeRelease2B} /></>,
    transactions: <Transactions {...common} onOpenTransaction={id => navigate(`/transactions/${encodeURIComponent(id)}`)} />,
    tasks: <TasksPage {...common} />,
    calendar: <CalendarPage {...common} />,
    forms: <FormsPage {...common} />
  };

  if (!loading && error) return <BootstrapFailure message={error} onRetry={load} />;
  let content = pages[route.page] || <ComingSoonPage />;
  if (route.page === "transactions" && route.transactionId) {
    const transaction = data.transactions.find(item => String(item.id) === String(route.transactionId));
    content = <TransactionCommandCenter transaction={transaction} transactionId={route.transactionId} user={data.user} tasks={data.transactionTasks || []} activity={data.transactionActivity || []} calendar={data.transactionCalendar || []} checklistPhaseDataAvailable={data.checklistPhaseDataAvailable} onBack={() => navigate("transactions")} />;
  }

  if (release2BOpen && release2BSession) {
    const record = release2BSession.record;
    const releaseMode = release2BSession.activeCorrection || `${record?.kind || "side"}:${record?.status || "choice"}:${normalizedRole}:${leadership ? "lead" : "member"}`;
    const leadershipWork = Boolean(leadership && record && (["Awaiting Leadership Approval", "Approved"].includes(record.status) || record.marketingSubmitted || record.openHouseSubmitted));
    const operationsWork = Boolean(normalizedRole === "transaction_coordinator" && record && ["Operations Intake", "Pre-List", "Listing", "Closed", "Sold"].includes(record.status));
    if (release2BSession.activeCorrection) content = <Release2BRecovery key={releaseMode} session={release2BSession} setSession={updateRelease2BSession} onExit={() => setRelease2BOpen(false)} />;
    else if (leadershipWork) content = <Release2BLeadershipReview key={releaseMode} session={release2BSession} setSession={updateRelease2BSession} onExit={() => setRelease2BOpen(false)} />;
    else if (operationsWork) content = <Release2BOperations key={releaseMode} session={release2BSession} setSession={updateRelease2BSession} onExit={() => setRelease2BOpen(false)} />;
    else content = <Release2BIntake key={releaseMode} session={release2BSession} setSession={updateRelease2BSession} user={data.user} leadership={leadership} onExit={() => setRelease2BOpen(false)} />;
  }

  return <AppShell user={data.user} page={route.page} onNavigate={navigate}>{loading ? <div className="loading">Loading ROOTS…</div> : content}{workflow && <DealIntakeModal workflow={workflow} user={data.user} agents={data.agents} leadership={leadership} onClose={() => setWorkflow(null)} onSaved={async () => { setWorkflow(null); await load(); navigate("transactions"); }} />}</AppShell>;
}
